import { initializeApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  getAuth,
  setPersistence,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateEmail,
  updatePassword,
  verifyPasswordResetCode,
  confirmPasswordReset,
  getIdToken,
  browserSessionPersistence,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  doc,
  query,
  getDocs,
  setDoc,
  updateDoc,
} from "firebase/firestore";

const config = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_DATABASE_URL,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID,
};

class Firebase {
  constructor() {
    this.firebaseApp = initializeApp(config);

    this.auth = getAuth(this.firebaseApp);
    this.db = getFirestore(this.firebaseApp);
    this.functions = getFunctions(this.firebaseApp);

    // **** Cloud functions ****
    this.getRedirect = httpsCallable(this.functions, "getUserRedirect");
    this.getUserCallable = httpsCallable(this.functions, "getUser");
    this.cleanupCompany = httpsCallable(this.functions, "cleanupCompany");
    this.createCompany = httpsCallable(this.functions, "createCompany");
    this.updateCompany = httpsCallable(this.functions, "updateCompany");
    this.uploadCompanyHandout = httpsCallable(
      this.functions,
      "uploadCompanyHandout"
    );
    this.setUserCallable = httpsCallable(this.functions, "setUser");
    this.userPlayedGame = httpsCallable(this.functions, "userPlayedGame");
    this.isUserWinner = httpsCallable(this.functions, "isUserWinner");
    this.updateUserCallable = httpsCallable(this.functions, "updateUser");
    this.getCompanyUsers = httpsCallable(this.functions, "getCompanyUsers");
    this.getCompanyStatistics = httpsCallable(
      this.functions,
      "getCompanyStatistics"
    );
    this.getCompanyWinners = httpsCallable(this.functions, "getCompanyWinners");
    this.deleteUser = httpsCallable(this.functions, "deleteCompanyUser");
    this.resetNewUserCount = httpsCallable(this.functions, "resetNewUserCount");
    this.verifyCompanyCode = httpsCallable(this.functions, "verifyCompanyCode");
    this.getCompanySettings = httpsCallable(
      this.functions,
      "getCompanySettings"
    );
    this.updateCompanySettings = httpsCallable(
      this.functions,
      "updateCompanySettings"
    );

    this.loading = false;
  }

  // **** Auth API's ****

  doCreateUserWithEmailAndPassword = (email, password) => {
    this.loading = true;
    return setPersistence(this.auth, browserSessionPersistence).then(() => {
      return createUserWithEmailAndPassword(this.auth, email, password);
    });
  };

  doSignInWithEmailAndPassword = (email, password) => {
    this.loading = true;
    return setPersistence(this.auth, browserSessionPersistence).then((res) => {
      return signInWithEmailAndPassword(this.auth, email, password).then(
        async (d) => {
          console.log("login success");
          const token = await getIdToken(this.auth.currentUser);
          console.debug(token);
        }
      );
    });
  };

  doSignOut = async () => await signOut(this.auth);
  doPasswordReset = (email) => sendPasswordResetEmail(this.auth, email);
  doPasswordUpdate = (password) =>
    updatePassword(this.auth.currentUser, password);
  doUpdateEmail = (email) => updateEmail(this.auth.currentUser, email);

  handleResetPassword = (actionCode, newPassword) => {
    // Verify the password reset code is valid.
    return verifyPasswordResetCode(this.auth, actionCode).then((email) => {
      return confirmPasswordReset(this.auth, actionCode, newPassword).then(
        (resp) => {
          // Password reset has been confirmed and new password updated.
        }
      );
    });
  };

  refreshUser = () => {
    if (this.auth.currentUser) {
      return getIdToken(this.auth.currentUser, true);
    } else {
      return Promise.resolve();
    }
  };

  resolveRedirect = async () => {
    const maxAttempts = 8;
    const delayMs = 1000;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      await this.refreshUser();
      const { data } = await this.getRedirect();

      if (!data.pending) {
        return data.redirect;
      }

      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }

    return "/UserDashboard";
  };

  // **** Info API's ****
  agreements = async () => {
    const q = query(collection(this.db, "Agreements"));

    const querySnapshot = await getDocs(q);
    return querySnapshot;
  };

  faq = async () => {
    const q = query(collection(this.db, "faq"));

    const querySnapshot = await getDocs(q);
    return querySnapshot;
  };

  // **** User API's ****
  // user = (uid) => this.db.collection("users").doc(`${uid}`);
  user = async (uid) => {
    const { data } = await this.getUserCallable({ uid });

    return {
      exists: () => data.exists,
      data: () => data.data,
    };
  };

  updateUser = async (uid, update) => {
    await this.updateUserCallable({ uid, update });
  };

  setUser = async (uid, data) => {
    await this.setUserCallable({ uid, userData: data });
  };

  // **** Company API's ****
  companies = () => ({
    get: async () => getDocs(collection(this.db, "companies")),
  });

  company = (companyCode) => ({
    set: (data) => setDoc(doc(this.db, "companies", companyCode), data),
    update: (data) => updateDoc(doc(this.db, "companies", companyCode), data),
  });
}

export default Firebase;
